import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../../mutation-types';
import KanbanCardsAPI from '../../../api/kanbanCards';

export const state = {
  records: [],
  archivedRecords: [],
  movementsByCardId: {},
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isFetchingArchived: false,
    isFetchingMovements: false,
  },
};

export const getters = {
  getCardsByList: _state => listId =>
    _state.records
      .filter(card => card.kanban_list_id === Number(listId))
      .sort((a, b) => a.position - b.position),
  getArchivedCards(_state) {
    return _state.archivedRecords;
  },
  getMovementsForCard: _state => cardId =>
    _state.movementsByCardId[cardId] || [],
  // `conversationId` here is the conversation's display_id, matching how the rest of the
  // frontend addresses conversations (see _conversation.json.jbuilder) -- not the raw
  // conversation_id primary key the kanban card payload also carries.
  getCardByConversationId: _state => conversationId =>
    _state.records.find(
      card => card.conversation.display_id === Number(conversationId)
    ),
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }, kanbanBoardId) => {
    commit(types.SET_KANBAN_CARDS_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanCardsAPI.get(kanbanBoardId);
      commit(types.SET_KANBAN_CARDS, response.data);
    } finally {
      commit(types.SET_KANBAN_CARDS_UI_FLAG, { isFetching: false });
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_KANBAN_CARDS_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanCardsAPI.update(id, updateObj);
      // `positions` is every card in the destination list, re-spaced by the server. Breaking a tie
      // shifts the whole column, so committing the moved card alone would leave its new siblings
      // sorted by stale positions.
      const { positions, ...card } = response.data;
      commit(types.EDIT_KANBAN_CARD, card);
      if (positions) commit(types.SET_KANBAN_CARD_POSITIONS, positions);
      return card;
    } finally {
      commit(types.SET_KANBAN_CARDS_UI_FLAG, { isUpdating: false });
    }
  },

  fetchArchived: async ({ commit }, params = {}) => {
    commit(types.SET_KANBAN_CARDS_UI_FLAG, { isFetchingArchived: true });
    try {
      const response = await KanbanCardsAPI.archived(params);
      commit(types.SET_KANBAN_ARCHIVED_CARDS, response.data);
    } finally {
      commit(types.SET_KANBAN_CARDS_UI_FLAG, { isFetchingArchived: false });
    }
  },

  fetchMovements: async ({ commit }, cardId) => {
    commit(types.SET_KANBAN_CARDS_UI_FLAG, { isFetchingMovements: true });
    try {
      const response = await KanbanCardsAPI.movements(cardId);
      commit(types.SET_KANBAN_CARD_MOVEMENTS, {
        cardId,
        movements: response.data,
      });
    } finally {
      commit(types.SET_KANBAN_CARDS_UI_FLAG, { isFetchingMovements: false });
    }
  },

  applyCableCard: ({ commit, state: _state }, cardPayload) => {
    const exists = _state.records.some(card => card.id === cardPayload.id);
    commit(
      exists ? types.EDIT_KANBAN_CARD : types.ADD_KANBAN_CARD,
      cardPayload
    );
  },

  applyCableArchive: ({ commit }, cardPayload) => {
    commit(types.DELETE_KANBAN_CARD, cardPayload.id);
  },
};

export const mutations = {
  [types.SET_KANBAN_CARDS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_KANBAN_CARDS]: MutationHelpers.set,
  [types.ADD_KANBAN_CARD]: MutationHelpers.create,
  [types.EDIT_KANBAN_CARD]: MutationHelpers.update,
  [types.DELETE_KANBAN_CARD]: MutationHelpers.destroy,
  // Patches positions by id, leaving every other field alone (mirrors SET_ARTICLE_POSITIONS).
  [types.SET_KANBAN_CARD_POSITIONS](_state, positionsHash) {
    _state.records = _state.records.map(card => {
      const position = positionsHash[card.id];
      return position === undefined ? card : { ...card, position };
    });
  },
  [types.SET_KANBAN_ARCHIVED_CARDS](_state, data) {
    _state.archivedRecords = data;
  },
  [types.SET_KANBAN_CARD_MOVEMENTS](_state, { cardId, movements }) {
    _state.movementsByCardId = {
      ..._state.movementsByCardId,
      [cardId]: movements,
    };
  },
};

export default { namespaced: true, state, getters, actions, mutations };
